import _ from 'lodash'
import moment from 'moment'
import { useContext, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import api from '../../services/api'
import {
  CompletionRateListContext,
  ExternalClickContext,
  StaticsContext,
} from '../../contexts'
import { REPORT_TYPE_ID_MAP } from '../../constants/report'
import useModal from '../../hooks/useModal'

import * as S from './Main.styles'

import { CRUDTable } from '../Tables'

import { ReactComponent as CheckIcon } from '../../assets/images/check_icon.svg'
import { ReactComponent as ModifyIcon } from '../../assets/images/crud/edit_icon.svg'

export default function TodayTodo() {
  const date = moment().format('YYYY-MM-DD')
  const mounted = useRef(false)
  const { openModal } = useModal()
  const [todoList, setTodoList] = useState(null)
  const [weeklyPlanShortcut, setWeeklyPlanShortcut] = useState(null)
  const { setCompleteRateList } = useContext(CompletionRateListContext)
  const externalClickEvent = useContext(ExternalClickContext)
  const { setStatics } = useContext(StaticsContext)

  useEffect(() => {
    if (! mounted.current) {
      mounted.current = true
      api.get(`/sessional-plans/todos/${date}`)
        .then(result =>  result.data)
        .then(({ todayTodoList }) => {
          setTodoList(todayTodoList)
          if (! todayTodoList.length) return

          const validTodayTodoList = _.filter(todayTodoList, todo => todo.dailyQuantityAmount > 0)
          if (! validTodayTodoList.length) return

          const { sessionalPlanId, totalQuantityId, weeklyQuantityId } = validTodayTodoList[0]
          api.get(`/sessional-plans/${sessionalPlanId}`)
            .then(result => {
              const totalQuantity = _.find(result.data.totalQuantityList, tq => tq.totalQuantityId === totalQuantityId)
              const { weekStartOn, weekEndOn } = _.find(totalQuantity.weeklyQuantityList, wq => wq.weeklyQuantityId === weeklyQuantityId)
              setWeeklyPlanShortcut(`planner/sessional-plan/${sessionalPlanId}/weekly-plan?weekStartOn=${moment(weekStartOn).format('YYYY-MM-DD')}&weekEndOn=${moment(weekEndOn).format('YYYY-MM-DD')}`)
            })
        })
        .catch(e => {
          console.error(e)
          openModal({ type: 'alert', props: { isError: true, contents: e.response?.data.message || e.message }})
        })
    } else {
      if (! externalClickEvent) return
      externalClickEvent.stopPropagation() // 이벤트 캡쳐링 방지
      if (! externalClickEvent.target.matches('input')) {
        todoList.forEach(todo => { todo.isUpdate = false })
        setTodoList(prevTodoList => [...prevTodoList])
      }
    }
  }, [externalClickEvent])

  /**
   * 오늘 할 일(계획) 달성량을 업데이트한다.
   * @param order 할 일 item order
   * @param sessionalPlanId 세셔널 플랜 idx
   * @param totalQuantityId 세션 총량 idx
   * @param weeklyQuantityId 주간 계획량 idx
   * @param dailyQuantityId 일일 계획량 idx
   * @param dailyQuantityAmount 오늘 계획량
   * @param completedQuantityAmount 오늘 계획 달성량
   * @returns {Promise<void>}
   */
  async function updateCompletedQuantity(order, sessionalPlanId, totalQuantityId, weeklyQuantityId, dailyQuantityId, dailyQuantityAmount, completedQuantityAmount) {
    try {
      if (completedQuantityAmount < 0) {
        return openModal({ type: 'alert', props: { isError: true, contents: '0보다 크거나 같은 숫자만 허용됩니다' }})
      }

      if (dailyQuantityAmount < completedQuantityAmount) {
        return openModal({ type: 'alert', props: { isError: true, contents: '계획량보다 큰 값이 존재해선 안 됩니다' }})
      }

      await api.patch(`/sessional-plans/${sessionalPlanId}/total-quantities/${totalQuantityId}/weekly-quantities/${weeklyQuantityId}/daily-quantities/${dailyQuantityId}/completed-quantities`
        , { completedQuantityAmount })

      todoList[order].completedQuantityAmount = completedQuantityAmount
      setTodoList([...todoList])

      const reportTypeId = await api.get(`/reports/infos/types/${date}`)
        .then(result => result.data.reportTypeList[0].reportTypeId || REPORT_TYPE_ID_MAP.DAILY)
        .catch(e => {
          console.error(e)
          const props = {
            type: 'alert',
            props: {
              isError: true,
              contents: e.response?.data.message || e.message
            }
          }
          openModal(props)
        })

      api.get(`/reports/infos/performances/${date}`, {
        params: { reportTypeId }
      }).then(result => { setStatics(result.data.statics) })

      api.get(`/reports/infos/performances/completion-rates/${moment(date).format('YYYY')}`)
        .then(result => {
          setCompleteRateList(result.data.completionRateList)
        })
    } catch (err) {
      console.error(err)
      openModal({ type: 'alert', props: { isError: true, contents: err.response?.data.message || err.message }})
    }
  }

  /**
   * 계획 달성량 수정하기를 클릭 시 input 태그가 열리도록 한다.
   * @param order 오늘 할일 item 순서
   * @param event 클릭 event
   */
  function clickCompletedQuantityUpdate(order, event) {
    event.stopPropagation() // 이벤트 캡쳐링 방지
    todoList[order].isUpdate = true
    setTodoList([...todoList])
  }

  return (
    <>
      <S.FlexContentsBox margin='10px 0 0 0'>
        <CheckIcon className='title-icon' width='30px'/>
        <S.Title fontSize='1.3em'>오늘 할 일</S.Title>
        <Link className='shortcut-btn' to={weeklyPlanShortcut}>
          위클리 플랜 바로 가기 >
        </Link>
        <CRUDTable>
          <colgroup>
            <col style={{ width: '25%' }} />   {/* 분류 */}
            <col style={{ width: '45%' }} />   {/* 영역 */}
            <col style={{ width: '15%' }} />   {/* 계획 */}
            <col style={{ width: '15%' }} />   {/* 완료 */}
          </colgroup>
          <tr>
            {
              ['분류', '영역', '계획', '완료'].map(head => {
                return <th>{head}</th>
              })
            }
          </tr>
          <tbody>
          {
            _.map(todoList, (todo, order) => {
              if (! todo?.dailyQuantityAmount) return
              return (
                <tr id='todo-rows'>
                  <td title={todo?.subjectCategory}>{todo?.subjectCategory}</td>
                  <td title={todo?.subject}>{todo?.subject}</td>
                  <td title={todo?.dailyQuantityAmount}>{todo?.dailyQuantityAmount}</td>
                  <td>
                    {
                      todo?.isUpdate
                        ?
                        <S.Input
                          defaultValue={todo?.completedQuantityAmount}
                          type='number'
                          min='0'
                          max={todo?.dailyQuantityAmount}
                          onBlur={(event) => updateCompletedQuantity(order, todo.sessionalPlanId, todo.totalQuantityId
                            , todo.weeklyQuantityId, todo.dailyQuantityId, todo.dailyQuantityAmount
                            , event.target.value)}
                          autoFocus
                        />
                        :
                        <>
                          {todo?.completedQuantityAmount}
                          <ModifyIcon className='modify-icon' fill='black' onClick={(event) => clickCompletedQuantityUpdate(order, event)}/>
                        </>
                    }
                  </td>
                </tr>
              )
            })
          }
          </tbody>
        </CRUDTable>
      </S.FlexContentsBox>
    </>
  )
}